import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  Chip,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CloudUpload,
  Api,
  Security,
  Dashboard,
  Description,
  IntegrationInstructions,
  Check,
  Star,
  Work,
  Business,
  CorporateFare,
} from '@mui/icons-material';

import { useAppStore } from '../store/appStore';
import { useTranslation } from '../store/languageStore';
import { colors, brand, primaryButtonSx } from '../theme';
import LoginModal from '../components/LoginModal';
// import RegisterModal from '../components/RegisterModal'; // Hidden - admin creates accounts
import LanguageSelector from '../components/LanguageSelector';
import bannerImage from '../assets/images/banner1.png';
import beoLogo from '../assets/images/beo-logo.png';




const LandingPage: React.FC = () => {
  const theme = useTheme();
  const { setShowLoginModal } = useAppStore();
  // setShowRegisterModal removed - admin creates accounts
  const { t } = useTranslation();

  const FEATURES = [
    {
      icon: <CloudUpload sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: t('landing.features.documentUpload.title'),
      description: t('landing.features.documentUpload.description'),
    },
    {
      icon: <IntegrationInstructions sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: t('landing.features.smartExtraction.title'),
      description: t('landing.features.smartExtraction.description'),
    },
    {
      icon: <Api sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: t('landing.features.apiIntegration.title'),
      description: t('landing.features.apiIntegration.description'),
    },
    {
      icon: <Security sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: t('landing.features.secureCompliant.title'),
      description: t('landing.features.secureCompliant.description'),
    },
  ];

  const DEMO_MODULES = [
    // In Progress Features
    { name: t('landing.modules.documentProcessing.name'), status: '', color: 'primary', description: t('landing.modules.documentProcessing.description') },
//     { name: t('landing.modules.tamperingDetectionModule.name'), status: '', color: 'error', description: t('landing.modules.tamperingDetectionModule.description') },
    // { name: t('landing.modules.emailProcessorModule.name'), status: t('landing.modules.status.inProgress'), color: 'warning', description: t('landing.modules.emailProcessorModule.description') },
    { name: t('landing.modules.tariffClassifierModule.name'), status: '', color: 'success', description: t('landing.modules.tariffClassifierModule.description') },
    { name: t('landing.modules.aiExportControl.name'), status: t('landing.modules.status.inProgress'), color: 'warning', description: t('landing.modules.aiExportControl.description') },
    { name: t('landing.modules.documentSets.name'), status: t('landing.modules.status.inProgress'), color: 'success', description: t('landing.modules.documentSets.description') },
    { name: t('landing.modules.magicMarkerPro.name'), status: t('landing.modules.status.inProgress'), color: 'secondary', description: t('landing.modules.magicMarkerPro.description') },
    { name: t('landing.modules.complianceEngine.name'), status: '', color: 'primary', description: t('landing.modules.complianceEngine.description') },

    // Coming Soon Features
    { name: t('landing.modules.aiLearningModule.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.aiLearningModule.description') },
    { name: t('landing.modules.businessIntelligence.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.businessIntelligence.description') },
    { name: t('landing.modules.conversationalAI.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.conversationalAI.description') },
    { name: t('landing.modules.workflowAutomation.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.workflowAutomation.description') },
    { name: t('landing.modules.blockchainIntegration.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.blockchainIntegration.description') },
    { name: t('landing.modules.multiLanguageSupport.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.multiLanguageSupport.description') },
    { name: t('landing.modules.masterDataManagement.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.masterDataManagement.description') },
    { name: t('landing.modules.predictiveAnalytics.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.predictiveAnalytics.description') },
    { name: t('landing.modules.smartContractGeneration.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.smartContractGeneration.description') },
    { name: t('landing.modules.intelligentDocumentRouting.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.intelligentDocumentRouting.description') },
    { name: t('landing.modules.supplyChainVisibility.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.supplyChainVisibility.description') },
    { name: t('landing.modules.documentQualityScoring.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.documentQualityScoring.description') },
    { name: t('landing.modules.versionControlSystem.name'), status: t('landing.modules.status.comingSoon'), color: 'warning', description: t('landing.modules.versionControlSystem.description') },
  ];

  const getFeatures = (key: string): string[] => {
    const features = t(key);
    return Array.isArray(features) ? features : [];
  };

  const PRICING_PLANS = [
    {
      name: t('landing.pricing.essential.name') as string,
      icon: <Work sx={{ fontSize: 40 }} />,
      price: '29',
      period: t('landing.pricing.month') as string,
      description: t('landing.pricing.essential.description') as string,
      popular: false,
      features: getFeatures('landing.pricing.essential.features'),
    },
    {
      name: t('landing.pricing.advanced.name') as string,
      icon: <Business sx={{ fontSize: 40 }} />,
      price: '79',
      period: t('landing.pricing.month') as string,
      description: t('landing.pricing.advanced.description') as string,
      popular: true,
      features: getFeatures('landing.pricing.advanced.features'),
    },
    {
      name: t('landing.pricing.enterprise.name') as string,
      icon: <CorporateFare sx={{ fontSize: 40 }} />,
      price: '199',
      period: t('landing.pricing.month') as string,
      description: t('landing.pricing.enterprise.description') as string,
      popular: false,
      features: getFeatures('landing.pricing.enterprise.features'),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: brand.yellow,
          color: brand.textPrimary,
          borderBottom: `1px solid ${brand.borderSubtle}`,
        }}
      >
        <Toolbar sx={{ py: 1, px: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            {/* Logo */}
            <Box
              component="img"
              src={beoLogo}
              alt="BEO Software"
              sx={{
                height: { xs: 32, sm: 40 },
                width: 'auto',
              }}
            />
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                color: brand.black,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {t('common.appName')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LanguageSelector />
            <Button
              variant="contained"
              onClick={() => setShowLoginModal(true)}
              sx={primaryButtonSx}
            >
              {t('landing.header.login')}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: `url(${bannerImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 8, md: 12 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${colors.gradientStart}d9 0%, ${colors.gradientEnd}d9 100%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                mb: 3,
                background: `linear-gradient(to right, #fff 0%, ${brand.sidebarActiveBg} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 20px rgba(0,0,0,0.1)',
              }}
            >
              {t('landing.hero.title')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 5,
                color: 'rgba(255,255,255,0.95)',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              {t('landing.hero.subtitle')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* Registration hidden - admin creates accounts */}
              {/* <Button
                variant="contained"
                size="large"
                onClick={() => setShowRegisterModal(true)}
                sx={{
                  backgroundColor: 'white',
                  color: '#667eea',
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 15px 50px rgba(0,0,0,0.3)',
                  },
                }}
              >
                {t('landing.hero.getStartedFree')}
              </Button> */}
              <Button
                variant="contained"
                size="large"
                onClick={() => setShowLoginModal(true)}
                sx={{
                  backgroundColor: 'white',
                  color: colors.primary,
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 15px 50px rgba(0,0,0,0.3)',
                  },
                }}
              >
                {t('landing.hero.signIn')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography
          variant="h3"
          textAlign="center"
          fontWeight={700}
          gutterBottom
          sx={{
            background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('landing.features.title')}
        </Typography>
        <Typography
          variant="h6"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 8, maxWidth: '700px', mx: 'auto' }}
        >
          {t('landing.features.subtitle')}
        </Typography>

        <Grid container spacing={4}>
          {FEATURES.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px ${colors.primary}33`,
                    borderColor: colors.primary,
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${colors.gradientStart}15 0%, ${colors.gradientEnd}15 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      transition: 'all 0.3s ease',
                      '.MuiCard-root:hover &': {
                        background: `linear-gradient(135deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
                        '& svg': {
                          color: 'white !important',
                        },
                      },
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" fontWeight={600} gutterBottom>
            {t('landing.howItWorks.title')}
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            {t('landing.howItWorks.subtitle')}
          </Typography>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: 'white',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: 24,
                    fontWeight: 'bold',
                  }}
                >
                  1
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('landing.howItWorks.step1.title')}
                </Typography>
                <Typography color="text.secondary">
                  {t('landing.howItWorks.step1.description')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: 'white',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.secondary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: 24,
                    fontWeight: 'bold',
                  }}
                >
                  2
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('landing.howItWorks.step2.title')}
                </Typography>
                <Typography color="text.secondary">
                  {t('landing.howItWorks.step2.description')}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: 'white',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.success.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: 24,
                    fontWeight: 'bold',
                  }}
                >
                  3
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t('landing.howItWorks.step3.title')}
                </Typography>
                <Typography color="text.secondary">
                  {t('landing.howItWorks.step3.description')}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Available Modules */}
      <Container maxWidth="lg" sx={{ py: 8, display: 'none' }}>
        <Typography variant="h3" textAlign="center" fontWeight={600} gutterBottom>
          {t('landing.modules.title')}
        </Typography>
        <Typography
          variant="h6"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 6 }}
        >
          {t('landing.modules.subtitle')}
        </Typography>

        <Grid container spacing={3} justifyContent="center">
          {DEMO_MODULES.map((module, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: 3,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '180px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    boxShadow: `0 12px 24px ${colors.primary}26`,
                    transform: 'translateY(-4px)',
                    borderColor: colors.primary,
                    '& .module-default': {
                      opacity: 0,
                      transform: 'translateY(-20px)',
                    },
                    '& .module-description': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                {/* Default View */}
                <Box
                  className="module-default"
                  sx={{
                    transition: 'all 0.3s ease',
                    opacity: 1,
                    transform: 'translateY(0)',
                  }}
                >
                  <Dashboard sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: '0.95rem' }}>
                    {module.name}
                  </Typography>
                  {module.status && (
                    <Chip
                      label={module.status}
                      color={module.color as any}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>

                {/* Hover Description */}
                <Box
                  className="module-description"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: 0,
                    transform: 'translateY(20px)',
                    transition: 'all 0.3s ease',
                    background: `linear-gradient(135deg, ${colors.gradientStart}0d 0%, ${colors.gradientEnd}0d 100%)`,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      lineHeight: 1.6,
                      fontWeight: 500,
                    }}
                  >
                    {module.description}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing Section */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8, display: 'none' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" textAlign="center" fontWeight={600} gutterBottom>
            {t('landing.pricing.title')}
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            {t('landing.pricing.subtitle')}
          </Typography>

          <Grid container spacing={4} justifyContent="center" sx={{ pt: 3 }}>
            {PRICING_PLANS.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ position: 'relative', height: '100%' }}>
                  {plan.popular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10,
                      }}
                    >
                      <Chip
                        label={t('landing.pricing.mostPopular')}
                        size="small"
                        icon={<Star sx={{ fontSize: 16 }} />}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          height: 28,
                          px: 1.5,
                          backgroundColor: colors.primary,
                          color: 'white',
                          boxShadow: `0 2px 8px ${colors.primary}4d`,
                          '& .MuiChip-icon': {
                            color: 'white',
                          },
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    </Box>
                  )}
                  <Card
                    elevation={plan.popular ? 8 : 2}
                    sx={{
                      height: '100%',
                      position: 'relative',
                      mt: plan.popular ? 2 : 0,
                      transition: 'all 0.3s ease',
                      transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                      border: plan.popular ? '2px solid' : '1px solid',
                      borderColor: plan.popular ? theme.palette.primary.main : 'grey.300',
                      '&:hover': {
                        transform: plan.popular ? 'scale(1.08)' : 'scale(1.03)',
                        boxShadow: plan.popular ? 12 : 6,
                      },
                    }}
                  >

                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    {/* Plan Header */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          color: plan.popular ? theme.palette.primary.main : 'text.primary',
                          mb: 2,
                        }}
                      >
                        {plan.icon}
                      </Box>
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{ color: plan.popular ? theme.palette.primary.main : 'text.primary' }}
                        gutterBottom
                      >
                        {plan.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {plan.description}
                      </Typography>
                    </Box>

                    {/* Pricing */}
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                        <Typography
                          variant="h3"
                          fontWeight={700}
                          sx={{ color: plan.popular ? theme.palette.primary.main : 'text.primary' }}
                        >
                          €{plan.price}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          /{plan.period}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Features List */}
                    <Box sx={{ textAlign: 'left', mb: 4 }}>
                      {plan.features.map((feature, featureIndex) => (
                        <Box
                          key={featureIndex}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1.5,
                          }}
                        >
                          <Check
                            sx={{
                              color: theme.palette.success.main,
                              fontSize: 20,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" color="text.primary">
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* CTA Button - Registration hidden, users must contact admin */}
                    <Button
                      variant={plan.popular ? 'contained' : 'outlined'}
                      size="large"
                      fullWidth
                      onClick={() => setShowLoginModal(true)}
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        backgroundColor: plan.popular ? theme.palette.primary.main : 'transparent',
                        color: plan.popular ? 'white' : theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: plan.popular ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.1),
                          borderColor: theme.palette.primary.dark,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {t('landing.hero.signIn')}
                    </Button>
                  </CardContent>
                </Card>
                </Box>
              </Grid>
            ))}
          </Grid>

        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ backgroundColor: 'grey.100', color: 'text.primary', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('common.appName')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('landing.footer.tagline')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('landing.footer.quickLinks')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  color="primary"
                  onClick={() => setShowLoginModal(true)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  {t('landing.footer.signIn')}
                </Button>
                {/* Registration hidden - admin creates accounts */}
                {/* <Button
                  color="primary"
                  onClick={() => setShowRegisterModal(true)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  {t('landing.footer.createAccount')}
                </Button> */}
              </Box>
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: 4,
              pt: 4,
              borderTop: '1px solid',
              borderColor: 'grey.300',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('landing.footer.copyright')}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Modals */}
      <LoginModal />
      {/* Registration modal hidden - admin creates accounts */}
      {/* <RegisterModal /> */}
    </Box>
  );
};

export default LandingPage;